import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.pexels.com/photos/5622211/pexels-photo-5622211.jpeg";
const recipeImg = (image_path) => {
  if (!image_path) return FALLBACK_IMG;
  const token = localStorage.getItem("access_token") || "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/files/${image_path}${token ? `?t=${encodeURIComponent(token)}` : ""}`;
};

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/recipes").then(({ data }) => setRecipes(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (r) => {
    if (!confirm(`Delete recipe "${r.name}"?`)) return;
    try { await api.delete(`/recipes/${r.recipe_id}`); toast.success("Recipe deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Recipes</p>
          <h1 className="text-3xl font-bold mt-1">Your recipes</h1>
          <p className="text-muted-foreground mt-1">Build recipes using foods from your database.</p>
        </div>
        <Link to="/recipes/new">
          <Button className="gap-2 rounded-full" data-testid="new-recipe-button"><Plus size={16} /> New recipe</Button>
        </Link>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> :
        recipes.length === 0 ? (
          <div className="nt-card p-12 text-center text-muted-foreground" data-testid="no-recipes">
            <BookOpen size={32} className="mx-auto mb-3 text-primary" />
            <p className="font-semibold">No recipes yet</p>
            <p className="text-sm mt-1">Create your first recipe.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recipes.map((r) => (
              <div key={r.recipe_id} className="nt-card overflow-hidden" data-testid={`recipe-card-${r.recipe_id}`}>
                <div className="aspect-video bg-muted relative">
                  <img src={recipeImg(r.image_path)} alt={r.name} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <Link to={`/recipes/${r.recipe_id}`} className="font-semibold hover:text-primary" data-testid={`recipe-link-${r.recipe_id}`}>{r.name}</Link>
                    <button onClick={() => del(r)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-recipe-${r.recipe_id}`}><Trash2 size={14} /></button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{r.ingredients?.length || 0} ingredients{r.calories ? ` • ${r.calories} kcal` : ""}</p>
                  {r.notes && <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{r.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
